import invariant from 'invariant';
import { useEffect, useState } from 'react';

import { LoadingSection } from 'components/LoadingSection/LoadingSection';
import { configValue } from 'config/appConfigUtils';
import { userDashboardApiEnableProfileFeatures } from 'modules/userDashboard/api//userDashboardApiEnableProfileFeatures';
import type {
  OpenWebOpenCustomUserProfileEvent,
  SpotImCurrentUserSentMessageSucceedEvent,
} from 'types/global/spotim';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { scrollIntoView } from 'utils/scrollIntoView';
import { showModal } from 'zustand-stores/modalStore';
import {
  setHasPreSignupComment,
  usePreSignupComment,
} from 'zustand-stores/preSignupCommentStore';

import { OpenWebContainer, OpenWebWidgetHolder } from './OpenWebWidget.styled';
import { useLoadOpenWeb } from './useLoadOpenWeb';

type Props = {
  postId: string;
  relativePath: string;
  userId?: string;
  hasIdealistProfileFeature: boolean;
  hasPublicProfile: boolean;
};

export function OpenWebWidget({
  postId,
  relativePath,
  userId,
  hasIdealistProfileFeature,
  hasPublicProfile,
}: Props) {
  const { hasPreSignupComment } = usePreSignupComment();

  const spotId = configValue('openwebSpotId');
  invariant(spotId, 'Spot id config missing');

  const appUrl = configValue('idealist', 'appUrl');
  const postUrl = `${appUrl}/${relativePath}`;

  // NOTE: we are resetting the hash since it's used only for sign-on and email verification
  // redirects to this page. Keeping the hash could break sharing, because the shared link
  // would always trigger scroll.
  useEffect(() => {
    if (window.location.hash !== '#openweb-container') return;
    setImmediate(() => window.history.pushState(null, '', '#'));
  }, []);

  useEffect(() => {
    const redirect = (event: OpenWebOpenCustomUserProfileEvent) => {
      const eventUserId = event.detail.userId;
      window.location.pathname = `/users/profile-by-id/${eventUserId}`;
    };

    document.addEventListener(
      'ow-open-custom-user-profile',
      redirect as EventListener,
    );

    return () => {
      document.removeEventListener(
        'ow-open-custom-user-profile',
        redirect as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    if (userId) return;

    const showSignup = () => {
      showModal('EMAIL_SIGNUP', {
        title: getText('Sign up to add a comment'),
        redirectTo: `${window.location.pathname}${window.location.search}#openweb-container`,
        onSignup: () => {
          setHasPreSignupComment(true);
        },
        onLogin: () => {
          setHasPreSignupComment(true);
        },
        requirePassword: true,
      });
    };

    document.addEventListener('spot-im-login-start', showSignup);

    return () => {
      document.removeEventListener('spot-im-login-start', showSignup);
    };
  }, [userId]);

  useEffect(() => {
    if (!userId || hasIdealistProfileFeature) return;

    const enableProfileFeatures = () => {
      userDashboardApiEnableProfileFeatures().then(() => {
        showModal('CREATE_USER_PROFILE', {});
      });
    };

    document.addEventListener('spot-im-login-start', enableProfileFeatures);

    return () => {
      document.removeEventListener(
        'spot-im-login-start',
        enableProfileFeatures,
      );
    };
  }, [hasIdealistProfileFeature, userId]);

  useEffect(() => {
    if (!userId || !hasIdealistProfileFeature || hasPublicProfile) return;

    const showCreateProfileModalWrapper = () =>
      showModal('CREATE_USER_PROFILE', {});

    document.addEventListener(
      'spot-im-login-start',
      showCreateProfileModalWrapper,
    );

    return () => {
      document.removeEventListener(
        'spot-im-login-start',
        showCreateProfileModalWrapper,
      );
    };
  }, [hasIdealistProfileFeature, hasPublicProfile, userId]);

  useEffect(() => {
    if (hasPreSignupComment && hasPublicProfile) {
      const element = document.getElementById('openweb-container');
      if (element) {
        scrollIntoView(element);
      }
      setHasPreSignupComment(null);
    }
  }, [hasPublicProfile, hasPreSignupComment]);

  useEffect(() => {
    const trackCommented = (e: SpotImCurrentUserSentMessageSucceedEvent) => {
      trackEvent('Comments: User Created a Comment', {
        type: e.detail.message_type,
      });
    };

    document.addEventListener(
      'spot-im-current-user-sent-message-succeed',
      trackCommented as EventListener,
    );

    return () =>
      document.removeEventListener(
        'spot-im-current-user-sent-message-succeed',
        trackCommented as EventListener,
      );
  }, []);

  useLoadOpenWeb({ spotId, userId, hasPublicProfile });

  const [isFocused, setIsFocused] = useState(false);
  const onFocus = () => {
    setIsFocused(true);
    setTimeout(() => setIsFocused(false), 4000);
  };

  useEffect(() => {
    document.addEventListener('idealist-comments-request-focus', onFocus, {
      once: true,
    });
  }, []);

  return (
    <OpenWebContainer
      data-qa-id="openweb-container"
      id="openweb-container"
      $isFocused={isFocused}
    >
      <OpenWebWidgetHolder
        data-spotim-module="conversation"
        data-spot-id={spotId}
        data-post-id={postId}
        data-post-url={postUrl}
      />

      <LoadingSection />
    </OpenWebContainer>
  );
}
